import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatRateBps } from '../checkout/money.js';
import { type Plan } from './billing.js';

/**
 * `W5-T07` §4.2, §7 — what a plan costs, as the server sent it: the monthly price from integer cents
 * and, when the price does not already include it, the tax line from basis points. Both go through
 * `W5-T15`'s `formatMoney` and `formatRateBps`, so no cent becomes a float (charter rule 2) and the
 * amount is never ours: it is the Stripe price's (`taxRateBps: null` is no tax rate configured).
 *
 * `testId` tells the page's price from the one in the switch dialog.
 */
export function PlanPrice({
  plan,
  locale,
  testId = 'billing',
}: {
  plan: Plan;
  locale: string;
  testId?: string;
}): ReactElement {
  const { t } = useTranslation();
  const amount = formatMoney({ amountCents: plan.amountCents, currency: plan.currency }, locale);

  return (
    <>
      <p className="mp-lead" data-testid={`${testId}-price`}>
        {t('billing.price.monthly', { amount })}
      </p>
      {plan.taxRateBps === null || plan.taxInclusive ? null : (
        <p className="mp-meta" data-testid={`${testId}-tax`}>
          {t('billing.price.plus_tax', { rate: formatRateBps(plan.taxRateBps, locale) })}
        </p>
      )}
    </>
  );
}
